import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import {
  TrendingUp, DollarSign, Users, AlertTriangle, Download, RefreshCw, Wrench, Receipt, Crown, Calendar,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOLD = '#D4AF37';
const RED = '#EF4444';
const GRAY = '#525252';
const PALETTE = ['#D4AF37', '#F59E0B', '#10B981', '#3B82F6', '#A855F7', '#EC4899'];

const PRESETS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'this_year', label: 'This Year' },
  { key: 'ytd', label: 'YTD' },
  { key: 'custom', label: 'Custom' },
];

const computeRange = (preset, customStart, customEnd) => {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const ymd = (y, m, d) => fmt(new Date(y, m, d));
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'this_month') {
    return { start: ymd(today.getFullYear(), today.getMonth(), 1), end: fmt(today) };
  }
  if (preset === 'last_month') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: fmt(first), end: fmt(last) };
  }
  if (preset === 'this_quarter') {
    const qStart = Math.floor(today.getMonth() / 3) * 3;
    return { start: ymd(today.getFullYear(), qStart, 1), end: fmt(today) };
  }
  if (preset === 'this_year' || preset === 'ytd') {
    return { start: ymd(today.getFullYear(), 0, 1), end: fmt(today) };
  }
  const days = PRESETS.find((p) => p.key === preset)?.days || 30;
  const start = new Date(today);
  start.setDate(start.getDate() - days);
  return { start: fmt(start), end: fmt(today) };
};

const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KpiCard = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <div data-testid={testId} className="bg-[#141414] border border-[#262626] rounded-lg p-4 flex items-start gap-3">
    {Icon && (
      <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${accent === 'red' ? 'bg-[#7F1D1D]/30 text-red-400' : 'bg-[#D4AF37]/15 text-[#D4AF37]'}`}>
        <Icon className="w-5 h-5" />
      </div>
    )}
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</p>
      <p className="text-white text-xl sm:text-2xl font-semibold mt-0.5 truncate">{value}</p>
      {sub && <p className="text-[#A3A3A3] text-xs mt-0.5">{sub}</p>}
    </div>
  </div>
);

const TabBtn = ({ active, onClick, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
      active ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'bg-[#141414] text-[#A3A3A3] border-[#262626] hover:text-white hover:border-[#D4AF37]/50'
    }`}
  >
    {label}
  </button>
);

const SectionCard = ({ title, action, children, testId }) => (
  <div data-testid={testId} className="bg-[#141414] border border-[#262626] rounded-lg p-4 sm:p-5">
    <div className="flex items-center justify-between mb-3 gap-2">
      <h3 className="text-white text-base sm:text-lg font-medium">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const tooltipStyle = {
  contentStyle: { background: '#0D0D0D', border: '1px solid #262626', borderRadius: 6, fontSize: 12, color: '#fff' },
  itemStyle: { color: '#fff' },
  labelStyle: { color: '#A3A3A3' },
};

const Reports = () => {
  const { user } = useAuth();
  const { activeDepartment } = useDepartment();
  const [preset, setPreset] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [granularity, setGranularity] = useState('day');
  const [tab, setTab] = useState('revenue');
  const [loading, setLoading] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [workforce, setWorkforce] = useState(null);
  const [ar, setAr] = useState(null);
  const [error, setError] = useState('');

  const range = useMemo(() => computeRange(preset, customStart, customEnd), [preset, customStart, customEnd]);
  const deptParam = ['service_manager', 'construction_manager'].includes(user?.role)
    ? '' // backend will scope
    : '';   // iter318 — global context is now a Trade; reports keep their own Division filter

  const qs = (extra = {}) => {
    const p = new URLSearchParams();
    if (range.start) p.set('start', range.start);
    if (range.end) p.set('end', range.end);
    if (deptParam) p.set('department', deptParam);
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  };

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [r, w, a] = await Promise.all([
        axios.get(`${API}/admin/reports/revenue?${qs({ granularity })}`, { withCredentials: true }),
        axios.get(`${API}/admin/reports/workforce?${qs()}`, { withCredentials: true }),
        axios.get(`${API}/admin/reports/ar?${deptParam ? `department=${deptParam}` : ''}`, { withCredentials: true }),
      ]);
      setRevenue(r.data);
      setWorkforce(w.data);
      setAr(a.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load reports');
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [preset, customStart, customEnd, granularity, activeDepartment]);

  const exportCsv = (type) => {
    const url = `${API}/admin/reports/export.csv?${qs({ type })}`;
    // Open in new tab — browser handles the download with cookies sent automatically
    window.open(url, '_blank');
  };

  // Drill-down helpers
  const goToInvoice = (id) => { window.location.href = `/admin/invoices?focus=${id}`; };
  const goToCustomer = (id) => { window.location.href = `/admin/customers?focus=${id}`; };

  return (
    <div data-testid="reports-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">INSIGHTS</p>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white">Reports</h1>
          <p className="text-[#A3A3A3] text-sm">Live operational and financial intelligence — tap any chart to drill in.</p>
        </div>
        <button
          data-testid="reports-refresh"
          onClick={fetchAll}
          className="self-start sm:self-end inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#D4AF37]/50 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-[#0D0D0D] border border-[#1f1f1f] rounded-lg p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <TabBtn
              key={p.key}
              testId={`reports-preset-${p.key}`}
              active={preset === p.key}
              onClick={() => setPreset(p.key)}
              label={p.label}
            />
          ))}
        </div>
        {preset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#525252]">Start</span>
              <input
                data-testid="reports-custom-start"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="mt-1 w-full bg-[#141414] border border-[#262626] text-white rounded-md h-10 px-3"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#525252]">End</span>
              <input
                data-testid="reports-custom-end"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="mt-1 w-full bg-[#141414] border border-[#262626] text-white rounded-md h-10 px-3"
              />
            </label>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#A3A3A3]">
          <span className="uppercase tracking-wider">Range:</span>
          <span className="text-white">{range.start || '—'}</span>
          <span>→</span>
          <span className="text-white">{range.end || '—'}</span>
          {deptParam && (
            <>
              <span className="ml-2 uppercase tracking-wider">Dept:</span>
              <span className="text-[#D4AF37] uppercase tracking-wider">{deptParam}</span>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <TabBtn testId="reports-tab-revenue" active={tab === 'revenue'} onClick={() => setTab('revenue')} label="Revenue" />
        <TabBtn testId="reports-tab-workforce" active={tab === 'workforce'} onClick={() => setTab('workforce')} label="Workforce" />
        <TabBtn testId="reports-tab-ar" active={tab === 'ar'} onClick={() => setTab('ar')} label="Money Owed" />
      </div>

      {error && (
        <div data-testid="reports-error" className="bg-[#7F1D1D]/30 border border-[#7F1D1D] text-red-200 rounded-md p-3 text-sm">{error}</div>
      )}

      {/* Revenue tab */}
      {tab === 'revenue' && revenue && (
        <div data-testid="reports-revenue-panel" className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard testId="kpi-paid-revenue" label="Paid Revenue" value={usd(revenue.totals.paid)} sub={`${revenue.totals.paid_count} invoices`} icon={DollarSign} />
            <KpiCard testId="kpi-unpaid" label="Unpaid Pipeline" value={usd(revenue.totals.unpaid)} sub={`${revenue.totals.unpaid_count} invoices`} icon={Receipt} />
            <KpiCard testId="kpi-avg-ticket" label="Avg Ticket" value={usd(revenue.totals.avg_ticket)} icon={TrendingUp} />
            <KpiCard testId="kpi-membership-arr" label="Membership ARR" value={usd(revenue.membership.annualized_revenue)} sub={`${revenue.membership.active_count} active members`} icon={Crown} />
          </div>

          <SectionCard
            testId="revenue-timeseries-card"
            title="Revenue Over Time"
            action={
              <div className="flex items-center gap-2">
                <div className="flex bg-[#0D0D0D] border border-[#262626] rounded-md overflow-hidden">
                  {['day', 'week', 'month'].map((g) => (
                    <button
                      key={g}
                      data-testid={`granularity-${g}`}
                      onClick={() => setGranularity(g)}
                      className={`px-2.5 py-1 text-xs ${granularity === g ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'text-[#A3A3A3] hover:text-white'}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="export-revenue-csv"
                  onClick={() => exportCsv('revenue')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#D4AF37]/50 text-xs"
                >
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
              </div>
            }
          >
            <div className="h-64 sm:h-72 -mx-2">
              {revenue.series.length === 0 ? (
                <p className="text-[#525252] text-sm text-center pt-12">No paid invoices in this range.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenue.series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                    <XAxis dataKey="bucket" stroke={GRAY} fontSize={11} interval="preserveStartEnd" />
                    <YAxis stroke={GRAY} fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
                    <Tooltip {...tooltipStyle} formatter={(v) => usd(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="paid" name="Paid" fill={GOLD} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unpaid" name="Unpaid" fill="#525252" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <SectionCard testId="department-revenue-card" title="Revenue by Department">
              <div className="h-56">
                {revenue.department_revenue.length === 0 ? (
                  <p className="text-[#525252] text-sm text-center pt-16">No department revenue yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={revenue.department_revenue}
                        dataKey="revenue"
                        nameKey="department"
                        cx="50%" cy="50%"
                        innerRadius={45} outerRadius={80}
                        paddingAngle={2}
                      >
                        {revenue.department_revenue.map((row, i) => (
                          <Cell key={row.department} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip {...tooltipStyle} formatter={(v) => usd(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>

            <SectionCard testId="membership-by-tier-card" title="Membership by Tier">
              {revenue.membership.by_tier.length === 0 ? (
                <p className="text-[#525252] text-sm text-center py-12">No active memberships.</p>
              ) : (
                <div className="space-y-2">
                  {revenue.membership.by_tier.map((m) => (
                    <div key={m.tier} className="flex items-center justify-between text-sm bg-[#0D0D0D] border border-[#1f1f1f] rounded-md px-3 py-2">
                      <div>
                        <span className="text-white uppercase font-medium tracking-wider">{m.tier}</span>
                        <span className="text-[#525252] ml-2">{m.count} members</span>
                      </div>
                      <span className="text-[#D4AF37]">{usd(m.annualized_revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      )}

      {/* Workforce tab */}
      {tab === 'workforce' && workforce && (
        <div data-testid="reports-workforce-panel" className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiCard testId="kpi-wf-revenue" label="Crew Revenue" value={usd(workforce.totals.revenue)} icon={DollarSign} />
            <KpiCard testId="kpi-wf-invoices" label="Invoices" value={workforce.totals.invoice_count} icon={Receipt} />
            <KpiCard testId="kpi-wf-jobs" label="Jobs Done" value={workforce.totals.jobs_completed} icon={Wrench} />
            <KpiCard testId="kpi-wf-hours" label="Labor Hours" value={workforce.totals.labor_hours.toFixed(1)} icon={Calendar} />
            <KpiCard testId="kpi-wf-mbr" label="Memberships Sold" value={workforce.totals.memberships_sold} icon={Crown} />
          </div>

          <SectionCard
            testId="tech-leaderboard-card"
            title="Technician Leaderboard"
            action={
              <button
                data-testid="export-workforce-csv"
                onClick={() => exportCsv('workforce')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#D4AF37]/50 text-xs"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            }
          >
            <div className="h-72 -mx-2">
              {workforce.rows.length === 0 ? (
                <p className="text-[#525252] text-sm text-center pt-16">No staff data in this range.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={workforce.rows.slice(0, 12)} margin={{ top: 10, right: 12, left: 0, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                    <XAxis dataKey="name" stroke={GRAY} fontSize={11} angle={-25} textAnchor="end" height={60} interval={0} />
                    <YAxis stroke={GRAY} fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
                    <Tooltip {...tooltipStyle} formatter={(v, name) => name === 'revenue' ? usd(v) : v} />
                    <Bar dataKey="revenue" name="Revenue" fill={GOLD} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {workforce.rows.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-[#525252] text-[10px] uppercase tracking-wider border-b border-[#1f1f1f]">
                      <th className="text-left py-2 px-2">Name</th>
                      <th className="text-left py-2 px-2 hidden sm:table-cell">Dept</th>
                      <th className="text-right py-2 px-2">Revenue</th>
                      <th className="text-right py-2 px-2">Invoices</th>
                      <th className="text-right py-2 px-2">Avg</th>
                      <th className="text-right py-2 px-2 hidden sm:table-cell">Jobs</th>
                      <th className="text-right py-2 px-2 hidden sm:table-cell">Hrs</th>
                      <th className="text-right py-2 px-2 hidden md:table-cell">Mbr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workforce.rows.map((r) => (
                      <tr key={r.id} data-testid={`tech-row-${r.id}`} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/40 cursor-pointer" onClick={() => goToCustomer(r.id)}>
                        <td className="py-2 px-2 text-white">{r.name}</td>
                        <td className="py-2 px-2 text-[#A3A3A3] hidden sm:table-cell uppercase text-xs tracking-wider">{r.department || '—'}</td>
                        <td className="py-2 px-2 text-right text-[#D4AF37]">{usd(r.revenue)}</td>
                        <td className="py-2 px-2 text-right text-white">{r.invoice_count}</td>
                        <td className="py-2 px-2 text-right text-white">{usd(r.avg_invoice)}</td>
                        <td className="py-2 px-2 text-right text-white hidden sm:table-cell">{r.jobs_completed}</td>
                        <td className="py-2 px-2 text-right text-white hidden sm:table-cell">{r.labor_hours.toFixed(1)}</td>
                        <td className="py-2 px-2 text-right text-white hidden md:table-cell">{r.memberships_sold}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* A/R tab */}
      {tab === 'ar' && ar && (
        <div data-testid="reports-ar-panel" className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard testId="kpi-open-invoices" label="Open Invoices" value={usd(ar.open_invoices.amount)} sub={`${ar.open_invoices.count} invoices`} icon={Receipt} accent={ar.open_invoices.count > 0 ? 'red' : null} />
            <KpiCard testId="kpi-over-90" label="Over 90 Days" value={usd(ar.aging.over_90.amount)} sub={`${ar.aging.over_90.count} invoices`} icon={AlertTriangle} accent={ar.aging.over_90.count > 0 ? 'red' : null} />
            <KpiCard testId="kpi-vip-payouts" label="VIP Payouts Owed" value={usd(ar.vip_payouts_owed.amount)} sub={`${ar.vip_payouts_owed.count} bonuses`} icon={Users} />
            <KpiCard testId="kpi-pending-estimates" label="Pending Estimates" value={usd(ar.pending_estimates.amount)} sub={`${ar.pending_estimates.count} estimates`} icon={Receipt} />
          </div>

          <SectionCard
            testId="aging-card"
            title="Invoice Aging"
            action={
              <button
                data-testid="export-ar-csv"
                onClick={() => exportCsv('ar')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#D4AF37]/50 text-xs"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            }
          >
            <div className="h-56 -mx-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { label: 'Current', amount: ar.aging.current.amount, count: ar.aging.current.count, fill: GOLD },
                    { label: '1-30', amount: ar.aging['1_30'].amount, count: ar.aging['1_30'].count, fill: '#F59E0B' },
                    { label: '31-60', amount: ar.aging['31_60'].amount, count: ar.aging['31_60'].count, fill: '#F97316' },
                    { label: '61-90', amount: ar.aging['61_90'].amount, count: ar.aging['61_90'].count, fill: '#EF4444' },
                    { label: '90+', amount: ar.aging.over_90.amount, count: ar.aging.over_90.count, fill: '#991B1B' },
                  ]}
                  margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                  <XAxis dataKey="label" stroke={GRAY} fontSize={11} />
                  <YAxis stroke={GRAY} fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
                  <Tooltip {...tooltipStyle} formatter={(v) => usd(v)} />
                  <Bar dataKey="amount" name="Outstanding" radius={[4, 4, 0, 0]}>
                    {[GOLD, '#F59E0B', '#F97316', '#EF4444', '#991B1B'].map((c) => <Cell key={c} fill={c} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <SectionCard testId="open-invoices-card" title={`Open Invoices (${ar.open_invoices.count})`}>
              {ar.open_invoices.rows.length === 0 ? (
                <p className="text-[#525252] text-sm py-8 text-center">All caught up. No open invoices.</p>
              ) : (
                <div className="overflow-y-auto max-h-80">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#141414]">
                      <tr className="text-[#525252] text-[10px] uppercase tracking-wider border-b border-[#1f1f1f]">
                        <th className="text-left py-2 px-2">Inv #</th>
                        <th className="text-left py-2 px-2">Customer</th>
                        <th className="text-right py-2 px-2">Amount</th>
                        <th className="text-right py-2 px-2">Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ar.open_invoices.rows.slice(0, 50).map((r) => (
                        <tr key={r.id} data-testid={`open-inv-${r.id}`} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/40 cursor-pointer" onClick={() => goToInvoice(r.id)}>
                          <td className="py-2 px-2 text-[#D4AF37]">{r.invoice_number}</td>
                          <td className="py-2 px-2 text-white truncate max-w-[160px]">{r.customer_name}</td>
                          <td className="py-2 px-2 text-right text-white">{usd(r.amount)}</td>
                          <td className={`py-2 px-2 text-right ${r.days_outstanding > 60 ? 'text-red-400' : r.days_outstanding > 30 ? 'text-orange-400' : 'text-white'}`}>{r.days_outstanding}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>

            <SectionCard testId="renewals-card" title={`Renewals Next 30 Days (${ar.membership_renewals_due.count})`}>
              {ar.membership_renewals_due.next_30_days.length === 0 ? (
                <p className="text-[#525252] text-sm py-8 text-center">No upcoming renewals.</p>
              ) : (
                <div className="overflow-y-auto max-h-80 space-y-2">
                  {ar.membership_renewals_due.next_30_days.map((r) => (
                    <div key={`${r.customer_id || r.customer_name}-${r.next_renewal}`} className="flex items-center justify-between text-sm bg-[#0D0D0D] border border-[#1f1f1f] rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-white truncate">{r.customer_name}</p>
                        <p className="text-[#525252] text-[10px] uppercase tracking-wider">{r.tier} · {r.billing_period}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] text-xs">{r.next_renewal}</p>
                        <p className="text-[#A3A3A3] text-[10px]">in {r.days_until} days</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      )}

      {loading && !revenue && (
        <div data-testid="reports-loading" className="text-center py-12 text-[#525252]">Loading reports…</div>
      )}
    </div>
  );
};

export default Reports;
